import type { ParseResult } from "papaparse"
import { parse } from "papaparse"

export type ColumnType = "number" | "string" | "date" | "day" | "week" | "month" | "quarter"
type Direction = "asc" | "desc"
export const dayOrder = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
export const monthOrder = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

// Shared regex patterns for column type detection and sorting
const dayRegex = new RegExp(`^(?:${dayOrder.join("|")})$`)
const monthRegex = new RegExp(`^(${monthOrder.join("|")})(?: (\\d{4}))*$`)
const weekRegex = /^(?:(\d{4})-)?W(\d{2})$/
const quarterRegex = /^Q([1-4])(?: (\d{4}))*$/
const dateRegex = /^\d{4}-\d{2}-\d{2}$/
const numberRegex = /^-?\d+(?:\.\d+)?$/

export function detectColumnType(data: string[][], colIndex: number): ColumnType {
  for (let i = 1; i < data.length; i++) {
    const val = data[i][colIndex]
    if (val.trim() !== "") {
      if (dayRegex.test(val)) {
        return "day"
      }
      if (monthRegex.test(val)) {
        return "month"
      }
      if (weekRegex.test(val)) {
        return "week"
      }
      if (quarterRegex.test(val)) {
        return "quarter"
      }
      if (dateRegex.test(val)) {
        return "date"
      }
      if (numberRegex.test(val)) {
        return "number"
      }
    }
  }
  return "string"
}

function sortNestedYear(a: string, b: string, direction: Direction) {
  if (a !== b) {
    const yearA = Number(a)
    const yearB = Number(b)
    return direction === "asc" ? yearA - yearB : yearB - yearA
  }
}

// Sorting handlers for each column type
const sortHandlers: Record<ColumnType, (a: string, b: string, direction: Direction) => number> = {
  number: (va, vb, direction) => {
    const na = Number(va)
    const nb = Number(vb)
    return direction === "asc" ? na - nb : nb - na
  },
  date: (va, vb, direction) => {
    const da = new Date(va).getTime()
    const db = new Date(vb).getTime()
    return direction === "asc" ? da - db : db - da
  },
  day: (va, vb, direction) => {
    const da = dayOrder.indexOf(va)
    const db = dayOrder.indexOf(vb)
    return direction === "asc" ? da - db : db - da
  },
  month: (va, vb, direction) => {
    const [, ma, ya] = va.match(monthRegex) || []
    const [, mb, yb] = vb.match(monthRegex) || []

    const sortedYear = sortNestedYear(ya, yb, direction)
    if (sortedYear) {
      return sortedYear
    }

    const idxA = monthOrder.indexOf(ma)
    const idxB = monthOrder.indexOf(mb)
    return direction === "asc" ? idxA - idxB : idxB - idxA
  },
  quarter: (va, vb, direction) => {
    const [, qa, ya] = va.match(quarterRegex) || []
    const [, qb, yb] = vb.match(quarterRegex) || []

    const sortedYear = sortNestedYear(ya, yb, direction)
    if (sortedYear) {
      return sortedYear
    }

    const qA = Number(qa)
    const qB = Number(qb)
    return direction === "asc" ? qA - qB : qB - qA
  },
  week: (va, vb, direction) => {
    const [, ya, wa] = va.match(weekRegex) || []
    const [, yb, wb] = vb.match(weekRegex) || []

    const sortedYear = sortNestedYear(ya, yb, direction)
    if (sortedYear) {
      return sortedYear
    }

    const weekA = Number(wa)
    const weekB = Number(wb)
    return direction === "asc" ? weekA - weekB : weekB - weekA
  },
  string: (va, vb, direction) => {
    if (va < vb) {
      return direction === "asc" ? -1 : 1
    }
    if (va > vb) {
      return direction === "asc" ? 1 : -1
    }
    return 0
  },
}

export function sortRows(
  rows: string[][],
  colIndex: number,
  colType: ColumnType,
  direction: Direction
): string[][] {
  return rows.slice().sort((a, b) => {
    const va = a[colIndex]
    const vb = b[colIndex]
    const handler = sortHandlers[colType] || sortHandlers.string
    return handler(va, vb, direction)
  })
}

export type CellData = string | TableData
export type TableData = CellData[][]

function getNestedData(contents: string, childData: string | string[]): string {
  if (!contents.startsWith("$nested")) {
    return contents
  }

  const parts = contents.split(":")
  const id = Number.parseInt(parts[1])
  return childData[id]
}

function parseNestedData(contents: string | string[][], childData: string | string[]): TableData | CellData {
  if (typeof contents === "string") {
    return getNestedData(contents, childData)
  }

  return contents.map((row) => row.map((data) => {
    const nestedContents = getNestedData(data, childData)
    const nested = parseNestedData(nestedContents, childData)
    return nested
  }))
}

export function parseData(data: string, childData: any) {
  const csvParse: ParseResult<string[]> = parse(data, {
    skipEmptyLines: true,
  })

  const csvData = csvParse.data

  const parsedChildData: string[] = childData.map((csv: string) => {
    const result = parse<string[]>(csv, { skipEmptyLines: true })
    return result.data
  })

  return parseNestedData(csvData, parsedChildData)
}
